import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Download, Search, Users, ClipboardList, TrendingUp, LogOut } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [filters, setFilters] = useState({ city: "", fuel_type: "", brand_tier: "" });
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);

  const load = useCallback(async (params = {}) => {
    setBusy(true);
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/leads`, { params, withCredentials: true }),
      ]);
      setStats(s.data);
      setLeads(l.data.leads || []);
    } catch (e) {
      if (e?.response?.status === 403 || e?.response?.status === 401) {
        setDenied(true);
      } else {
        toast.error("Errore nel caricare i dati");
      }
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login", { state: { from: "/admin" }, replace: true });
      return;
    }
    load();
  }, [user, loading, navigate, load]);

  const applyFilters = (e) => {
    e.preventDefault();
    const params = {};
    if (filters.city) params.city = filters.city;
    if (filters.fuel_type) params.fuel_type = filters.fuel_type;
    if (filters.brand_tier) params.brand_tier = filters.brand_tier;
    load(params);
  };

  const downloadCsv = async () => {
    try {
      const r = await axios.get(`${API}/admin/leads.csv`, {
        responseType: "blob",
        withCredentials: true,
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "auto-advisor-leads.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore export CSV");
    }
  };

  if (denied) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="admin-denied">
        <div className="text-center">
          <p className="text-lg text-[#666]">Accesso riservato agli amministratori.</p>
          <Link to="/" className="mt-4 inline-block text-[#0055FF]">Torna alla home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="admin-page">
      <header className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="font-heading text-xl font-semibold tracking-tight">
          auto<span className="text-[#0055FF]">.</span>advisor <span className="text-sm text-[#666] font-normal">/ admin</span>
        </Link>
        <button onClick={logout} className="inline-flex items-center gap-2 text-sm text-[#666] hover:text-[#111]" data-testid="admin-logout">
          <LogOut className="w-4 h-4" /> Esci
        </button>
      </header>

      <main className="max-w-7xl mx-auto px-6 pt-4 pb-24">
        <h1 className="font-heading text-4xl font-semibold tracking-tight">Dashboard Lead</h1>
        <p className="mt-2 text-[#666]">Consulta i profili acquisiti e scaricali per la cessione a terzi.</p>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard label="Lead totali" value={stats?.leads ?? "—"} icon={ClipboardList} />
          <StatCard label="Utenti registrati" value={stats?.users ?? "—"} icon={Users} />
          <StatCard label="Ricerche generate" value={stats?.recommendations ?? "—"} icon={TrendingUp} />
        </div>

        <div className="mt-10 flex flex-col md:flex-row md:items-end gap-4 justify-between">
          <form onSubmit={applyFilters} className="grid grid-cols-1 sm:grid-cols-4 gap-3 flex-1" data-testid="admin-filters">
            <Input placeholder="Città" value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} data-testid="filter-city" />
            <select className="h-10 rounded-md border border-[#EAEAEA] bg-white px-3 text-sm" value={filters.fuel_type} onChange={(e) => setFilters({ ...filters, fuel_type: e.target.value })} data-testid="filter-fuel">
              <option value="">Tutte alimentazioni</option>
              <option value="petrol">Benzina</option>
              <option value="diesel">Diesel</option>
              <option value="hybrid">Ibrida</option>
              <option value="electric">Elettrica</option>
              <option value="lpg">GPL</option>
              <option value="any">Indifferente</option>
            </select>
            <select className="h-10 rounded-md border border-[#EAEAEA] bg-white px-3 text-sm" value={filters.brand_tier} onChange={(e) => setFilters({ ...filters, brand_tier: e.target.value })} data-testid="filter-brand">
              <option value="">Tutte fasce</option>
              <option value="utilitaria">Utilitaria</option>
              <option value="premium">Premium</option>
              <option value="luxury">Luxury</option>
            </select>
            <button type="submit" className="h-10 rounded-md bg-[#111] text-white hover:bg-[#333] inline-flex items-center justify-center gap-2" data-testid="admin-apply-filters">
              <Search className="w-4 h-4" /> Filtra
            </button>
          </form>
          <button onClick={downloadCsv} className="h-10 px-5 rounded-md bg-[#0055FF] text-white hover:bg-[#0044dd] inline-flex items-center gap-2 whitespace-nowrap" data-testid="admin-export-csv">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>

        <div className="mt-6 rounded-2xl border border-[#EAEAEA] bg-white overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="admin-leads-table">
            <thead className="bg-[#F9F9FB] text-[#666] text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">Data</th>
                <th className="px-4 py-3 text-left">Score</th>
                <th className="px-4 py-3 text-left">Nome</th>
                <th className="px-4 py-3 text-left">Contatti</th>
                <th className="px-4 py-3 text-left">Città</th>
                <th className="px-4 py-3 text-left">Budget</th>
                <th className="px-4 py-3 text-left">Alim.</th>
                <th className="px-4 py-3 text-left">Fascia</th>
                <th className="px-4 py-3 text-left">Cons.</th>
              </tr>
            </thead>
            <tbody>
              {busy && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-[#666]">Caricamento…</td></tr>
              )}
              {!busy && leads.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-[#666]">Nessun lead trovato</td></tr>
              )}
              {leads.map((l, i) => (
                <tr key={l.lead_id} className={i % 2 ? "bg-[#F9F9FB]" : ""} data-testid={`admin-lead-row-${i}`}>
                  <td className="px-4 py-3 whitespace-nowrap text-[#666]">
                    {new Date(l.created_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                      l.tier === "hot" ? "bg-red-100 text-red-700"
                      : l.tier === "warm" ? "bg-amber-100 text-amber-700"
                      : "bg-gray-100 text-gray-700"
                    }`} data-testid={`admin-lead-tier-${i}`}>{(l.tier || "—").toUpperCase()} · {l.score ?? 0}</span>
                  </td>
                  <td className="px-4 py-3 font-medium">{l.name}</td>
                  <td className="px-4 py-3">
                    <div>{l.email}</div>
                    <div className="text-[#666] text-xs">{l.phone}</div>
                  </td>
                  <td className="px-4 py-3">{l.preferences?.city || "—"}</td>
                  <td className="px-4 py-3">{l.preferences?.budget_eur ? `${new Intl.NumberFormat("it-IT").format(l.preferences.budget_eur)} €` : "—"}</td>
                  <td className="px-4 py-3 capitalize">{l.preferences?.fuel_type || "—"}</td>
                  <td className="px-4 py-3 capitalize">{l.preferences?.brand_tier || "—"}</td>
                  <td className="px-4 py-3 text-xs">
                    {l.consent_marketing ? "MKT " : ""}
                    {l.consent_transfer ? "CED" : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-[#EAEAEA] bg-white p-5 flex items-center gap-4">
      <div className="w-11 h-11 rounded-xl bg-[#E5EEFF] text-[#0055FF] flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-xs uppercase tracking-widest text-[#666] font-medium">{label}</p>
        <p className="font-heading text-2xl font-semibold">{value}</p>
      </div>
    </div>
  );
}
